import React from 'react';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import useGetWaiverRequest from '../../../hooks/waivers/useWaiverRequest';
import {
  EmployeeWaiverQuestions,
  getEmployeeWaiverQuestionsSchema,
  WaiverResponseData,
} from '../../../types/waivers';
import TextInput from '../../form/base/TextInput';
import TextareaInput from '../../form/base/TextareaInput';
import SignatureInputControlled from '../../form/base/SignatureInputControlled';
import CheckboxInput from '../../form/base/CheckboxInput';
import Document from '../../global/Document';
import useSubmitEmployeeWaiver from '../../../hooks/waivers/useSubmitEmployeeWaiver';
import ColoredAlert from '../../global/ColoredAlert';
import useStorageUrl from '../../../hooks/global/useStorageUrl';

interface Props {
  waiverRequestId: string;
}

/**
 * This component is used to view waivers that are already submitted by the customer.
 * If the waiver requires actions from the employee, it will show a form to complete the waiver.
 */
function WaiverRequestView({ waiverRequestId }: Props) {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const { data, refetch, isFetching } = useGetWaiverRequest({
    waiverRequestId,
  });
  const { waiverRequest, waiver } = data!;

  const { url: pdfUrl } = useStorageUrl(waiverRequest.signedFileLink, {
    gcTime: 0,
    staleTime: 0,
  });

  const sigCanvas = React.useRef<SignatureCanvas | null>(null);

  const isSubmittedByCustomer =
    waiverRequest.status === 'SIGNED' && !!waiverRequest.signedFileLink;

  const isEmployeeSettingsEnabled =
    !!waiver?.employeeSettings?.enabled &&
    !!waiver?.employeeSettings.options.length;
  const isEmployeeWaiverCompleted =
    isEmployeeSettingsEnabled &&
    !!waiverRequest.previousSignedFileLink &&
    !!(waiverRequest.responseData as WaiverResponseData).employee;
  const isEmployeeWaiverRequired =
    isSubmittedByCustomer &&
    isEmployeeSettingsEnabled &&
    !isEmployeeWaiverCompleted;

  const height =
    isLarge && !isEmployeeWaiverRequired
      ? 500
      : isLarge || !isEmployeeWaiverRequired
      ? 400
      : 300;

  const {
    submitWaiverMutation,
    isPending,
    isSuccess: isCompleted,
  } = useSubmitEmployeeWaiver({
    waiverRequest,
    waiver,
    onError: () => {
      toast.error('Failed to submit waiver');
    },
    onSuccess: async () => {
      await refetch();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const validationSchema = getEmployeeWaiverQuestionsSchema(
    waiver.employeeSettings?.options ?? [],
  );

  const initialValues = waiver.employeeSettings?.options.reduce(
    (acc, option) => {
      const isTextOrSignature = [
        'TEXT',
        'MULTILINE_TEXT',
        'SIGNATURE',
      ].includes(option.type);
      acc[option.id] = isTextOrSignature ? '' : false;
      return acc;
    },
    {} as Record<string, string | boolean>,
  );

  const onSignatureEnd = (
    optionId: string,
    helpers: FormikHelpers<EmployeeWaiverQuestions>,
  ) => {
    if (!sigCanvas.current) return;
    const isEmpty = sigCanvas.current.isEmpty();
    if (isEmpty) return;
    const trimmedCanvas = sigCanvas.current.getTrimmedCanvas();
    const url = trimmedCanvas.toDataURL('image/png');
    if (!url) return;
    helpers.setFieldValue(optionId, url);
  };

  const onSignatureClear = (
    optionId: string,
    helpers: FormikHelpers<EmployeeWaiverQuestions>,
  ) => {
    helpers.setFieldValue(optionId, '');
  };

  // Handle in parent
  if (!isSubmittedByCustomer) return null;

  return (
    <Stack gap={2} width="100%" maxWidth="768px" mx="auto" position="relative">
      {isSubmittedByCustomer &&
        (!isEmployeeSettingsEnabled || isEmployeeWaiverCompleted) && (
          <ColoredAlert
            type="success"
            title="Waiver Completed"
            subtitle="You can view the completed waiver below."
            dismissible={false}
          />
        )}

      {isEmployeeWaiverRequired && (
        <ColoredAlert
          type="info"
          title="Action Required"
          subtitle="The customer has completed their portion of the waiver. Please complete your portion to finalize the waiver."
          dismissible
          animate={false}
        />
      )}

      <Document height={height} fileUrl={pdfUrl} isLoading={isFetching} />

      {isEmployeeWaiverRequired && (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount
          validateOnChange
          validateOnBlur
          onSubmit={async (values, helpers) => {
            try {
              helpers.setSubmitting(true);
              await submitWaiverMutation.mutateAsync(values);
            } catch (error) {
              // Empty
            } finally {
              helpers.setSubmitting(false);
            }
          }}>
          {helpers => {
            const { isSubmitting, isValid, isValidating } = helpers;
            return (
              <Form
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}>
                {(waiver.employeeSettings?.options ?? []).map(question => {
                  const isFullWidth = [
                    'TEXT',
                    'MULTILINE_TEXT',
                    'SIGNATURE',
                  ].includes(question.type);
                  return (
                    <Stack
                      key={question.id}
                      width={isFullWidth ? '100%' : 'auto'}>
                      {question.type === 'TEXT' ? (
                        <TextInput
                          label={question.text}
                          name={question.id}
                          isLabelMultiline
                        />
                      ) : question.type === 'MULTILINE_TEXT' ? (
                        <TextareaInput
                          label={question.text}
                          name={question.id}
                          isLabelMultiline
                        />
                      ) : question.type === 'SIGNATURE' ? (
                        <SignatureInputControlled
                          label={question.text}
                          name={question.id}
                          alwaysShowClear
                          sigCanvas={sigCanvas}
                          onEnd={() => onSignatureEnd(question.id, helpers)}
                          onClear={() => onSignatureClear(question.id, helpers)}
                        />
                      ) : question.type === 'CHECKBOX' ? (
                        <CheckboxInput
                          label={question.text}
                          name={question.id}
                        />
                      ) : null}
                    </Stack>
                  );
                })}
                <Stack
                  width="calc(100% + 1px)"
                  py={2}
                  bgcolor="white"
                  position="sticky"
                  bottom={0}
                  left={0}
                  right={0}>
                  <LoadingButton
                    loading={isSubmitting || isPending || isCompleted}
                    variant="base"
                    type="submit"
                    disabled={
                      !isValid ||
                      isSubmitting ||
                      isPending ||
                      isCompleted ||
                      isValidating
                    }>
                    <span>Submit</span>
                  </LoadingButton>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      )}
    </Stack>
  );
}

export default WaiverRequestView;
